import { decisionOutput } from './nodeKinds';

/**
 * A Decision model step's output is its questions: one enum per question,
 * the question's outcomes. Editing the questions rebuilds the output, and
 * any edge that names a question or an outcome that is gone is repaired
 * (the outcomes that still exist are kept) or dropped, so the step never
 * routes on something it no longer answers. The default edge is untouched.
 */

function repairCondition(when, output) {
  if (when && Array.isArray(when.all)) {
    // A route on several answers: repair each part; a part that can no longer
    // match drops the whole route, since the route would never be taken.
    const parts = when.all.map((c) => repairCondition(c, output));
    if (parts.some((c) => c === null)) return null;
    return parts.length === 1 ? parts[0] : { all: parts };
  }
  if (!when || typeof when.field !== 'string') return when;   // not a shape this editor writes: left as saved
  const spec = output[when.field];
  if (!spec) return null;
  if (spec.type === 'number') {
    // A score range: kept, clamped to the scale (a level removed shrinks it).
    if (when.op === 'between' && when.range) {
      const min = Math.max(spec.min, Math.min(when.range.min, spec.max));
      const max = Math.min(spec.max, when.range.max);
      return min < max ? { ...when, range: { ...when.range, min, max, inclusive_max: max >= spec.max ? true : when.range.inclusive_max } } : null;
    }
    if (['lt', 'lte', 'gt', 'gte'].includes(when.op)) return when.value >= spec.min && when.value <= spec.max ? when : null;
    return null;
  }
  if (spec.type !== 'enum') return null;
  const values = spec.values || [];
  if (when.op === 'in' || when.op === 'not_in') {
    const value = (Array.isArray(when.value) ? when.value : []).filter((v) => values.includes(v));
    return value.length ? { ...when, value } : null;
  }
  if (when.op === 'eq' || when.op === 'neq') return values.includes(when.value) ? when : null;
  return null;
}

export function repairEdges(edges, output) {
  const out = [];
  for (const e of edges || []) {
    if (e.default !== undefined || !e.when) { out.push(e); continue; }
    const when = repairCondition(e.when, output);
    if (when) out.push(when === e.when ? e : { ...e, when });
  }
  return out;
}

/** The node patch for a new question map: config, output and edges together. */
export function withQuestions(node, questions) {
  const output = decisionOutput(questions);
  return { config: { ...(node.config || {}), questions }, output, edges: repairEdges(node.edges || [], output) };
}
